import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { Logo } from '../../components/brand/Logo'
import { Icon } from '../../components/ui/icons'
import { CLINIC, WHATSAPP_URL } from '../../config/clinic'

/**
 * Cascarón de las páginas públicas de agendamiento: header con la marca,
 * stepper siempre visible y una barra de acción fija abajo en el móvil
 * (patrón que RedSalud hace bien). Sobrio y de una columna: acá la persona
 * viene a reservar, no a leer.
 */

export const BOOKING_STEPS = ['¿Qué necesitas?', 'Elige tu hora', 'Identifícate y confirma'] as const

export function BookingShell({
  step,
  children,
  action,
  title,
}: {
  /** Paso actual (1..3); 4 = confirmación (stepper completo). */
  step: number
  children: ReactNode
  /** Barra de acción fija abajo (CTA principal + volver). */
  action?: ReactNode
  title?: string
}) {
  return (
    <div className="flex min-h-screen flex-col bg-surface text-ink">
      <header className="border-b border-steel-100 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3">
          <Link to="/" aria-label="Ir al sitio de Cima Sport">
            <Logo className="h-9" />
          </Link>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-steel-700 hover:text-brand-700"
          >
            <Icon name="whatsapp" className="h-4 w-4 text-[#25D366]" />
            <span className="hidden sm:inline">¿Prefieres WhatsApp?</span>
            <span className="sm:hidden">WhatsApp</span>
          </a>
        </div>
      </header>

      <Stepper step={step} />

      <main className={clsx('mx-auto w-full max-w-3xl flex-1 px-4 pt-5', action ? 'pb-28' : 'pb-10')}>
        {title && <h1 className="font-display text-2xl font-bold text-ink md:text-3xl">{title}</h1>}
        {children}
      </main>

      {action && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-steel-100 bg-white/95 backdrop-blur">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3">{action}</div>
        </div>
      )}

      <footer className="border-t border-steel-100 bg-white py-3 text-center text-xs text-steel-500">
        {CLINIC.name} · {CLINIC.address} · {CLINIC.phone}
      </footer>
    </div>
  )
}

function Stepper({ step }: { step: number }) {
  return (
    <nav aria-label="Pasos de la reserva" className="border-b border-steel-100 bg-white">
      <ol className="mx-auto flex max-w-3xl items-center px-4 py-2.5">
        {BOOKING_STEPS.map((label, i) => {
          const n = i + 1
          const state = n < step ? 'done' : n === step ? 'current' : 'todo'
          return (
            <li key={label} className="flex min-w-0 flex-1 items-center gap-2" aria-current={state === 'current' ? 'step' : undefined}>
              <span
                className={clsx(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                  state === 'done' && 'bg-brand-600 text-white',
                  state === 'current' && 'bg-brand-600 text-white ring-4 ring-brand-100',
                  state === 'todo' && 'bg-steel-100 text-steel-500',
                )}
              >
                {state === 'done' ? <Icon name="check" className="h-3.5 w-3.5" /> : n}
              </span>
              <span
                className={clsx(
                  'truncate text-xs font-medium sm:text-sm',
                  state === 'current' ? 'text-ink' : 'text-steel-500',
                  // En móvil solo se lee el paso actual; los otros son el número.
                  state !== 'current' && 'hidden sm:inline',
                )}
              >
                {label}
              </span>
              {i < BOOKING_STEPS.length - 1 && <span className="mx-1 h-px flex-1 bg-steel-200" aria-hidden />}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

/** Avatar de iniciales: TODO-NEGOCIO fotos reales de los profesionales. */
export function ProfessionalAvatar({ firstName, lastName, className }: { firstName: string; lastName: string; className?: string }) {
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  return (
    <span
      className={clsx(
        'flex shrink-0 items-center justify-center rounded-full bg-brand-100 font-display text-sm font-bold text-brand-700',
        className ?? 'h-10 w-10',
      )}
      aria-hidden
    >
      {initials}
    </span>
  )
}

/** Botón principal de la barra fija. */
export function PrimaryAction({
  children,
  onClick,
  disabled,
  type = 'button',
  form,
}: {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  type?: 'button' | 'submit'
  form?: string
}) {
  return (
    <button
      type={type}
      form={form}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-brand-600 px-6 py-3 font-display text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-brand-300 sm:flex-none"
    >
      {children}
    </button>
  )
}

export function BackAction({ onClick, label = 'Volver' }: { onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-3 text-sm font-medium text-steel-700 hover:text-brand-700"
    >
      <Icon name="arrowLeft" className="h-4 w-4" />
      {label}
    </button>
  )
}
